import type { User } from 'firebase/auth';
import { useEffect, useRef } from 'react';
import { analyticsClient } from '@/lib';
import {
  PWA_INSTALLED_EVENT,
  PWA_INSTALLED_KEY,
  PWA_PENDING_INSTALL_KEY,
  recordPwaInstall,
} from '@/lib/pwaInstall';

function dispatchPwaInstalled(isFirst: boolean) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(PWA_INSTALLED_EVENT, { detail: { isFirst } }));
}

export function usePwaInstallRecorder(user: User | null) {
  const userRef = useRef(user);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    const handleBeforeInstallPrompt = () => {
      analyticsClient.capture('pwa_install_prompt');
    };

    const handleAppInstalled = () => {
      const isFirst = !localStorage.getItem(PWA_INSTALLED_KEY);
      localStorage.setItem(PWA_INSTALLED_KEY, '1');

      const timestamp = new Date().toISOString();
      const currentUser = userRef.current;
      if (currentUser) {
        recordPwaInstall(currentUser.uid, timestamp);
      } else {
        localStorage.setItem(PWA_PENDING_INSTALL_KEY, timestamp);
      }

      analyticsClient.capture('pwa_installed', { is_first_install: isFirst });
      dispatchPwaInstalled(isFirst);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    if (!user) return;

    const pendingInstall = localStorage.getItem(PWA_PENDING_INSTALL_KEY);
    if (pendingInstall) {
      recordPwaInstall(user.uid, pendingInstall);
      localStorage.removeItem(PWA_PENDING_INSTALL_KEY);
    }
  }, [user]);
}
